import React, { useEffect, useState } from "react";
import { Button, Input, Spinner } from "reactstrap";

import { TrackingEvent } from "@/@types/ShipmentTracking";
import { getTrackingDetails } from "@/services/track-shipment";
import { listOfCarriers } from "@/utils/constants";
import useAlertBar from "@/hooks/useAlertBar";
import classes from "@/styles/track-shipment/track-shipment.module.css";
import useGoogleAnalytics from "@/hooks/useGoogleAnalytics";

interface TrackShipmentMainProps {
  trackingId?: string;
  carrier?: string;
}

const TrackShipmentMain: React.FC<TrackShipmentMainProps> = ({
  trackingId,
  carrier,
}) => {
  const [shippingCarrier, setShippingCarrier] = useState<string>(carrier || "");
  const [trackingNumber, setTrackingNumber] = useState<string>(
    trackingId || ""
  );
  const [trackingDetails, setTrackingDetails] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { openAlertBar } = useAlertBar();
  const { sendCustomEvent } = useGoogleAnalytics();

  useEffect(() => {
    if (trackingId) {
      setTrackingNumber(trackingId);
    }
  }, [trackingId]);

  useEffect(() => {
    if (carrier) {
      setShippingCarrier(carrier);
    }
  }, [carrier]);

  const handleSubmit = async () => {
    setIsLoading(true);

    sendCustomEvent("trackShipment", {
      page: {
        url:
          document.location.origin +
          document.location.pathname +
          document.location.search,
      },
      params: { trackingNumber, shippingCarrier },
    });

    try {
      const body = { trackingNumber, shippingCarrier };
      const { data } = await getTrackingDetails(body);

      if (data.status) {
        let details = `
          ${shippingCarrier.toUpperCase()}
          Tracking No: ${trackingNumber}

          ============ Track Events ==============

          `;
        data.data.events.forEach(
          (e: TrackingEvent) =>
            (details += `
              Date & Time: ${e.date ?? ""}
              Location: ${e.location}
              Description: ${e.description}
          `)
        );

        setTrackingDetails(details);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        openAlertBar(data.message, false);
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
      openAlertBar(`Unexpected error occurred: ${error}`, false);
    }
  };

  return (
    <main className={classes.container}>
      <section className={classes.hero}>
        <div className={` container`}>
          <h1 className="h2 text-secondary">Track My Shipment</h1>
        </div>
      </section>
      <section>
        <div className={`container my-5`}>
          <div className="d-flex gap-3 mb-3 flex-wrap">
            {listOfCarriers.map(({ label, value }) => (
              <div key={value}>
                <Input
                  id={value}
                  type="radio"
                  name="carrier"
                  value={value}
                  checked={shippingCarrier === value}
                  onChange={() => setShippingCarrier(value)}
                />
                <label htmlFor={value}>&nbsp;{label}</label>
              </div>
            ))}
          </div>
          <div className="mb-3">
            <label htmlFor="number">Tracking Number</label>
            <Input
              value={trackingNumber}
              onChange={({ target }) => setTrackingNumber(target.value)}
              id="number"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="details">Details</label>
            <textarea
              className="form-control"
              rows={10}
              value={trackingDetails}
              readOnly
              id="details"
            />
          </div>
          <Button
            color="primary"
            variant="contained"
            disabled={isLoading}
            onClick={handleSubmit}
            className="btn-green d-block mx-auto btn-lg"
          >
            Submit{" "}
            {isLoading && (
              <span style={{ marginLeft: "10px" }}>
                <Spinner size="sm" />
              </span>
            )}
          </Button>
        </div>
      </section>
    </main>
  );
};

export default TrackShipmentMain;
